// v4 HighlightAnnouncement (VG1-gaps) — v3
// src/components/common/highlightAnnouncement/HighlightAnnouncement.tsx
// recomposed on tokens. Wire parity: same announcements list query
// (active_highlights, published_at desc, page size 1), same localStorage
// dismiss ledger (`dismissed_announcements`, number[]), same CRUD-page
// suppression, same click split (author URL → safe external open; otherwise
// /whats-new#announcement-<id>). Mount gates live in AppShell (v3 AppV1
// parity: web + non-embedded + admin/teacher + whats-new/announcements flag).
import { CloseCircleLinear } from '@assessprep/icons/solar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { APRenderHtml } from '@/components/ap/renderHtml';
import { Button } from '@/components/ui/button';
import { openExternalUrlSafely } from '@/lib/urlSafety';
import { getVideoThumbnailUrl } from '@/pages/announcements/utils';
import { useList } from '@refinedev/core';

// v3 byte-parity: same key + number[] shape so dismissals survive the shell flip.
const DISMISSED_KEY = 'dismissed_announcements';

const getDismissedIds = (): number[] => {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(Number).filter(Number.isFinite);
  } catch {
    return [];
  }
};

const persistDismissedId = (id: number) => {
  try {
    const ids = getDismissedIds();
    ids.push(id);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    // Ignore localStorage errors (v3 parity).
  }
};

const CRUD_PATH_SEGMENTS = ['/create', '/edit', '/clone', '/new'];

interface HighlightItem {
  id: number;
  title: string;
  body?: string;
  url?: string;
  video_url?: string;
}

const VideoThumb = ({ url, alt }: { url: string; alt: string }) => {
  const thumbUrl = getVideoThumbnailUrl(url);
  return thumbUrl ? (
    <img
      src={thumbUrl}
      alt={alt}
      className="h-40 w-full rounded-md object-cover"
      data-testid="v4-highlight-video-thumb"
    />
  ) : (
    <div
      aria-hidden="true"
      className="flex h-40 w-full items-center justify-center rounded-md bg-muted text-2xl text-muted-foreground"
    >
      ▶
    </div>
  );
};

export const HighlightAnnouncement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissedIds, setDismissedIds] = useState<number[]>(getDismissedIds);

  const isOnCrudPage = CRUD_PATH_SEGMENTS.some((seg) => location.pathname.includes(seg));

  const { data } = useList({
    resource: 'announcements',
    filters: [{ field: 'active_highlights', operator: 'eq', value: true }],
    sorters: [{ field: 'published_at', order: 'desc' }],
    pagination: { pageSize: 1 },
  });

  const announcements: HighlightItem[] = Array.isArray(data?.data)
    ? data.data.map((item: Record<string, any>) => {
        const attrs = item?.attributes ?? item;
        return {
          id: attrs.id as number,
          title: attrs.title as string,
          body: attrs.body as string | undefined,
          url: attrs.url as string | undefined,
          video_url: attrs.video_url as string | undefined,
        };
      })
    : [];

  const handleDismiss = (id: number) => {
    setDismissedIds((prev) => [...prev, id]);
    persistDismissedId(id);
  };

  const handleLearnMore = (announcement: HighlightItem) => {
    handleDismiss(announcement.id);
    if (announcement.url) {
      // Author-supplied URL served to every user — http(s)-only + noopener.
      openExternalUrlSafely(announcement.url);
    } else {
      navigate(`/whats-new#announcement-${announcement.id}`);
    }
  };

  const current = announcements.find((a) => !dismissedIds.includes(a.id));
  if (!current || isOnCrudPage) return null;

  return (
    <div
      role="dialog"
      aria-label={current.title}
      className="fixed bottom-6 end-6 z-50 flex w-90 max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
      data-testid="v4-highlight-announcement"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border bg-ap-muted-a50 py-2 pe-2 ps-4">
        <span className="min-w-0 truncate text-sm font-semibold" data-testid="v4-highlight-title">
          {current.title}
        </span>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={t('CommonText.close')}
          onClick={() => handleDismiss(current.id)}
          data-testid="v4-highlight-dismiss"
        >
          <CloseCircleLinear aria-hidden="true" className="size-4" />
        </Button>
      </div>
      <div className="flex flex-col gap-2.5 px-4 py-3">
        {current.video_url && <VideoThumb url={current.video_url} alt={current.title} />}
        {current.body && (
          <APRenderHtml
            text={current.body}
            sanitize
            math={false}
            className="max-h-40 overflow-y-auto text-sm text-muted-foreground"
            data-testid="v4-highlight-body"
          />
        )}
      </div>
      <div className="flex justify-end px-4 pb-3">
        <Button size="sm" onClick={() => handleLearnMore(current)} data-testid="v4-highlight-cta">
          {t('Announcements.learn_more')}
        </Button>
      </div>
    </div>
  );
};
